import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  GraduationCap,
  UserPlus,
  ArrowLeft,
  Trash2,
  CheckCircle2,
  BookOpen,
  Clock,
  MessageSquare,
} from "lucide-react";
import { API, authFetch } from "@/App";
import { SURAH_LIST } from "@/data/quranMeta";

/**
 * /teacher — Parent/Teacher dashboard.
 *
 * - Prompts the user to become a teacher if they aren't one.
 * - Lets teachers add students by registered email, and assign per-surah
 *   lesson ranges with an optional note.
 * - Each student row expands to show assigned lessons and per-lesson status.
 */
export default function Teacher() {
  const navigate = useNavigate();
  const [isTeacher, setIsTeacher] = useState(null);
  const [students, setStudents] = useState([]);
  const [expanded, setExpanded] = useState(null); // student_id currently open
  const [lessonsByStudent, setLessonsByStudent] = useState({});
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [assignOpen, setAssignOpen] = useState(null); // student_id for dialog
  const [form, setForm] = useState({
    surah: 1,
    fromAyah: 1,
    toAyah: "",
    note: "",
  });

  const refresh = useCallback(async () => {
    try {
      const meRes = await authFetch(`${API}/teacher/me`);
      const me = meRes.ok ? await meRes.json() : { is_teacher: false };
      setIsTeacher(!!me.is_teacher);
      if (me.is_teacher) {
        const r = await authFetch(`${API}/teacher/students`);
        const data = r.ok ? await r.json() : { students: [] };
        setStudents(data.students || []);
      }
    } catch {
      setIsTeacher(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadLessons = async (sid) => {
    try {
      const r = await authFetch(`${API}/teacher/students/${sid}/lessons`);
      if (r.ok) {
        const data = await r.json();
        setLessonsByStudent((m) => ({ ...m, [sid]: data.lessons || [] }));
      }
    } catch (err) {
      console.error("loadLessons failed:", err);
    }
  };

  const toggleExpand = (sid) => {
    setExpanded((prev) => (prev === sid ? null : sid));
    if (!lessonsByStudent[sid]) loadLessons(sid);
  };

  const becomeTeacher = async () => {
    try {
      const r = await authFetch(`${API}/teacher/become`, { method: "POST" });
      if (r.ok) {
        setIsTeacher(true);
        toast.success("You're a teacher now");
      } else {
        toast.error("Could not enable teacher mode");
      }
    } catch {
      toast.error("Network error");
    }
  };

  const addStudent = async () => {
    if (!addEmail.trim()) return;
    try {
      const r = await authFetch(`${API}/teacher/students`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: addEmail.trim().toLowerCase(),
          display_name: addName.trim() || undefined,
        }),
      });
      if (r.ok) {
        setAddEmail("");
        setAddName("");
        toast.success("Student added");
        refresh();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Could not add student");
      }
    } catch {
      toast.error("Network error");
    }
  };

  const removeStudent = async (sid) => {
    if (!window.confirm("Remove this student and all their lessons?")) return;
    try {
      const r = await authFetch(`${API}/teacher/students/${sid}`, {
        method: "DELETE",
      });
      if (r.ok) {
        toast.success("Student removed");
        refresh();
      }
    } catch {
      toast.error("Network error");
    }
  };

  const assignLesson = async () => {
    const sid = assignOpen;
    if (!sid) return;
    const surahMeta = SURAH_LIST.find((s) => s.number === form.surah);
    const from = parseInt(form.fromAyah) || 1;
    const to = form.toAyah ? parseInt(form.toAyah) : null;
    if (to != null && to < from) {
      toast.error("To-ayah must be ≥ From-ayah");
      return;
    }
    if (from > (surahMeta?.ayahs || 300)) {
      toast.error("From-ayah is beyond end of surah");
      return;
    }
    try {
      const r = await authFetch(
        `${API}/teacher/students/${sid}/lessons`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            surah_number: form.surah,
            from_ayah: from,
            to_ayah: to,
            note: form.note.trim() || null,
          }),
        },
      );
      if (r.ok) {
        toast.success("Lesson assigned");
        setAssignOpen(null);
        setForm({ surah: 1, fromAyah: 1, toAyah: "", note: "" });
        loadLessons(sid);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Could not assign");
      }
    } catch {
      toast.error("Network error");
    }
  };

  const removeLesson = async (lesson_id, sid) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      const r = await authFetch(`${API}/teacher/lessons/${lesson_id}`, {
        method: "DELETE",
      });
      if (r.ok) loadLessons(sid);
    } catch (err) {
      console.error("removeLesson failed:", err);
    }
  };

  const [feedbackEdit, setFeedbackEdit] = useState({}); // { lesson_id: text }
  const [feedbackOpen, setFeedbackOpen] = useState(null); // lesson_id being edited

  const saveFeedback = async (lesson_id, sid) => {
    const text = feedbackEdit[lesson_id] ?? "";
    try {
      const r = await authFetch(
        `${API}/teacher/lessons/${lesson_id}/feedback`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ feedback: text }),
        },
      );
      if (r.ok) {
        toast.success(text.trim() ? "Feedback sent" : "Feedback cleared");
        setFeedbackOpen(null);
        loadLessons(sid);
      } else {
        toast.error("Could not save feedback");
      }
    } catch {
      toast.error("Network error");
    }
  };

  const selectedSurahMeta = SURAH_LIST.find((s) => s.number === form.surah);

  if (isTeacher === null) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center text-slate-400 font-outfit">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-islamic-blue-950 px-4 py-6">
      <div className="max-w-3xl mx-auto">
        <button
          data-testid="teacher-back"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-gold-400 mb-6 font-outfit text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gold-400/15 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-gold-400" />
          </div>
          <div>
            <h1 className="font-cinzel text-2xl text-slate-100">
              Parent / Teacher
            </h1>
            <p className="text-slate-500 text-sm font-outfit">
              Assign Quran lessons to your students or children.
            </p>
          </div>
        </div>

        {!isTeacher && (
          <div
            data-testid="become-teacher-card"
            className="glass-card rounded-xl p-6 text-center"
          >
            <p className="text-slate-300 font-outfit mb-4">
              Enable teacher mode to add students and assign lessons.
            </p>
            <Button
              data-testid="become-teacher-btn"
              onClick={becomeTeacher}
              className="btn-gold"
            >
              Become a Teacher
            </Button>
          </div>
        )}

        {isTeacher && (
          <>
            {/* Add student */}
            <div className="glass-card rounded-xl p-5 mb-6">
              <p className="font-cinzel text-slate-200 text-sm mb-3 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-gold-400" /> Add a student
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="add-student-email"
                  type="email"
                  placeholder="student@email.com (must be registered)"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  className="bg-islamic-blue-900/60 border-white/10 text-slate-200"
                />
                <Input
                  data-testid="add-student-name"
                  placeholder="Display name (optional)"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  className="bg-islamic-blue-900/60 border-white/10 text-slate-200 sm:max-w-[200px]"
                />
                <Button
                  data-testid="add-student-btn"
                  onClick={addStudent}
                  className="btn-gold"
                >
                  Add
                </Button>
              </div>
              <p className="text-slate-500 text-xs font-outfit mt-2">
                Students must already have an account. They'll see assigned
                lessons on their Dashboard.
              </p>
            </div>

            {/* Students list */}
            {students.length === 0 ? (
              <p className="text-slate-500 text-sm font-outfit text-center py-8">
                No students yet — add one above to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {students.map((s) => {
                  const expandedNow = expanded === s.student_id;
                  const lessons = lessonsByStudent[s.student_id] || [];
                  return (
                    <div
                      key={s.student_id}
                      data-testid={`student-row-${s.student_id}`}
                      className="glass-card rounded-xl overflow-hidden"
                    >
                      <div className="p-4 flex items-center justify-between gap-3">
                        <button
                          data-testid={`student-expand-${s.student_id}`}
                          onClick={() => toggleExpand(s.student_id)}
                          className="flex-1 text-left"
                        >
                          <p className="font-outfit text-slate-100">
                            {s.student_name || s.student_email}
                          </p>
                          <p className="font-outfit text-slate-500 text-xs">
                            {s.student_email}
                          </p>
                        </button>
                        <Button
                          data-testid={`assign-lesson-btn-${s.student_id}`}
                          size="sm"
                          onClick={() => setAssignOpen(s.student_id)}
                          className="btn-gold text-xs"
                        >
                          Assign lesson
                        </Button>
                        <button
                          data-testid={`remove-student-${s.student_id}`}
                          onClick={() => removeStudent(s.student_id)}
                          className="text-slate-500 hover:text-red-400 p-2"
                          aria-label="Remove student"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {expandedNow && (
                        <div className="border-t border-white/5 bg-islamic-blue-950/40 p-4">
                          {lessons.length === 0 ? (
                            <p className="text-slate-500 text-xs font-outfit">
                              No lessons yet.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {lessons.map((l) => {
                                const sMeta = SURAH_LIST.find(
                                  (x) => x.number === l.surah_number,
                                );
                                const editingFeedback = feedbackOpen === l.lesson_id;
                                return (
                                  <li
                                    key={l.lesson_id}
                                    className="flex flex-col gap-2 p-3 rounded-lg bg-islamic-blue-900/40"
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-outfit text-slate-200 text-sm flex items-center gap-2">
                                          <BookOpen className="w-3 h-3 text-gold-400 shrink-0" />
                                          {sMeta?.englishName || l.surah_number}{" "}
                                          <span className="text-slate-500">
                                            {l.from_ayah}–{l.to_ayah ?? "end"}
                                          </span>
                                          {l.completed && (
                                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                                          )}
                                        </p>
                                        {l.note && (
                                          <p className="text-slate-400 text-xs mt-1 font-outfit">
                                            {l.note}
                                          </p>
                                        )}
                                        {l.feedback && !editingFeedback && (
                                          <p
                                            data-testid={`lesson-feedback-${l.lesson_id}`}
                                            className="text-gold-300 text-xs mt-1 font-outfit italic flex items-start gap-1.5"
                                          >
                                            <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                            {l.feedback}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 shrink-0">
                                        <button
                                          data-testid={`edit-feedback-btn-${l.lesson_id}`}
                                          onClick={() => {
                                            setFeedbackEdit((m) => ({
                                              ...m,
                                              [l.lesson_id]: l.feedback || "",
                                            }));
                                            setFeedbackOpen(
                                              editingFeedback ? null : l.lesson_id,
                                            );
                                          }}
                                          className="text-slate-500 hover:text-gold-400 p-1"
                                          title="Add / edit feedback"
                                          aria-label="Edit feedback"
                                        >
                                          <MessageSquare className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          data-testid={`remove-lesson-${l.lesson_id}`}
                                          onClick={() =>
                                            removeLesson(l.lesson_id, s.student_id)
                                          }
                                          className="text-slate-500 hover:text-red-400 p-1"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                    {editingFeedback && (
                                      <div className="flex flex-col gap-2 bg-islamic-blue-950/60 rounded-md p-2">
                                        <Textarea
                                          data-testid={`feedback-input-${l.lesson_id}`}
                                          rows={2}
                                          placeholder="Write feedback for your student (e.g., Great job on 2:1–5, now try 2:6–10)"
                                          value={feedbackEdit[l.lesson_id] ?? ""}
                                          onChange={(e) =>
                                            setFeedbackEdit((m) => ({
                                              ...m,
                                              [l.lesson_id]: e.target.value,
                                            }))
                                          }
                                          className="bg-islamic-blue-800 border-white/10 text-slate-200 resize-none text-xs"
                                        />
                                        <div className="flex justify-end gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setFeedbackOpen(null)}
                                            className="text-slate-400 text-xs h-7"
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            data-testid={`save-feedback-${l.lesson_id}`}
                                            size="sm"
                                            onClick={() =>
                                              saveFeedback(l.lesson_id, s.student_id)
                                            }
                                            className="btn-gold text-xs h-7"
                                          >
                                            Save
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Assign-lesson dialog */}
        <Dialog
          open={!!assignOpen}
          onOpenChange={(v) => !v && setAssignOpen(null)}
        >
          <DialogContent
            data-testid="assign-lesson-dialog"
            className="bg-islamic-blue-900 border-white/10 text-slate-200"
          >
            <DialogHeader>
              <DialogTitle className="font-cinzel text-gold-400">
                Assign a lesson
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Pick a surah + ayah range and add an optional note.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs font-outfit mb-1 block">
                  Surah
                </label>
                <Select
                  value={String(form.surah)}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, surah: parseInt(v), fromAyah: 1, toAyah: "" }))
                  }
                >
                  <SelectTrigger
                    data-testid="lesson-surah"
                    className="bg-islamic-blue-800 border-white/10 text-slate-200"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-72">
                    {SURAH_LIST.map((s) => (
                      <SelectItem
                        key={s.number}
                        value={String(s.number)}
                        className="text-slate-200"
                      >
                        {s.number}. {s.englishName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs font-outfit mb-1 block">
                    From ayah
                  </label>
                  <Input
                    data-testid="lesson-from-ayah"
                    type="number"
                    min={1}
                    max={selectedSurahMeta?.ayahs || 1}
                    value={form.fromAyah}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, fromAyah: e.target.value }))
                    }
                    className="bg-islamic-blue-800 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-outfit mb-1 block">
                    To ayah (blank = end)
                  </label>
                  <Input
                    data-testid="lesson-to-ayah"
                    type="number"
                    min={1}
                    max={selectedSurahMeta?.ayahs || 1}
                    placeholder={`end (${selectedSurahMeta?.ayahs || "?"})`}
                    value={form.toAyah}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, toAyah: e.target.value }))
                    }
                    className="bg-islamic-blue-800 border-white/10"
                  />
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-outfit mb-1 block">
                  Note (optional)
                </label>
                <Textarea
                  data-testid="lesson-note"
                  rows={3}
                  placeholder="e.g., Memorize by Friday; focus on tajweed of ra."
                  value={form.note}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, note: e.target.value }))
                  }
                  className="bg-islamic-blue-800 border-white/10 text-slate-200 resize-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setAssignOpen(null)}
                  className="text-slate-400"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="lesson-submit-btn"
                  onClick={assignLesson}
                  className="btn-gold"
                >
                  Assign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
